import { useState } from 'react';
import type { FormEvent, ReactNode } from 'react';
import { Trans, useTranslation } from 'react-i18next';

import { Button, ButtonWithLoading } from '@actual-app/components/button';
import { Card } from '@actual-app/components/card';
import { View } from '@actual-app/components/view';

import { FinancialText } from '#components/FinancialText';
import type {
  FinancialGoalRequest,
  FinancialManagementState,
  ProductFinanceClient,
} from '#components/product-finance/client';
import {
  formatMoneyInput,
  formatProductMoney,
  parseMoneyInput,
} from '#components/product-finance/money';

import { getManagementErrorMessage } from './management-errors';
import {
  assertNever,
  buttonRowStyle,
  errorStyle,
  Field,
  formStyle,
  helpStyle,
  inputStyle,
  ManagementCard,
} from './management-shared';

export type ManagedGoal = FinancialManagementState['goals'][number];

export type GoalKind = 'emergency-fund' | 'custom-target';

export type EmergencyStrategy =
  | 'salary-multiple'
  | 'essential-expense-months'
  | 'fixed-amount';

export function GoalManagement({
  state,
  client,
  onChanged,
}: Readonly<{
  state: FinancialManagementState;
  client: ProductFinanceClient;
  onChanged: () => Promise<void>;
}>) {
  const [editing, setEditing] = useState<ManagedGoal | null>(null);
  const [isCreating, setIsCreating] = useState(false);

  return (
    <View style={{ gap: 14 }}>
      <ManagementCard title={<Trans>Financial goals</Trans>}>
        <p style={helpStyle}>
          <Trans>
            Define the emergency fund target here, then protect, use, and
            replenish it from the dashboard.
          </Trans>
        </p>
        <Button
          variant="primary"
          onPress={() => {
            setEditing(null);
            setIsCreating(true);
          }}
          style={{ alignSelf: 'flex-start', minHeight: 42 }}
        >
          <Trans>Add goal</Trans>
        </Button>
      </ManagementCard>
      {(isCreating || editing !== null) && (
        <GoalForm
          key={editing?.goal.id ?? 'new'}
          state={state}
          editing={editing}
          client={client}
          onCancel={() => {
            setEditing(null);
            setIsCreating(false);
          }}
          onSaved={async () => {
            setEditing(null);
            setIsCreating(false);
            await onChanged();
          }}
        />
      )}
      {state.goals.length === 0 ? (
        <ManagementCard title={<Trans>Goals</Trans>}>
          <p style={helpStyle}>
            <Trans>No financial goals yet.</Trans>
          </p>
        </ManagementCard>
      ) : (
        state.goals.map(goal => (
          <GoalCard
            key={goal.goal.id}
            summary={goal}
            client={client}
            onEdit={() => {
              setIsCreating(false);
              setEditing(goal);
            }}
            onChanged={onChanged}
          />
        ))
      )}
    </View>
  );
}

export function GoalCard({
  summary,
  client,
  onEdit,
  onChanged,
}: Readonly<{
  summary: ManagedGoal;
  client: ProductFinanceClient;
  onEdit: () => void;
  onChanged: () => Promise<void>;
}>) {
  const { t } = useTranslation();
  const [isDeleting, setIsDeleting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function remove() {
    if (!globalThis.confirm(t('Delete this financial goal?'))) return;
    setIsDeleting(true);
    setError(null);
    try {
      await client.deleteFinancialGoal({ id: summary.goal.id });
      await onChanged();
    } catch {
      setError(t('Unable to delete this financial goal.'));
    } finally {
      setIsDeleting(false);
    }
  }

  return (
    <Card style={{ margin: 0 }}>
      <View style={{ padding: 16, gap: 8 }}>
        <View
          style={{
            flexDirection: 'row',
            justifyContent: 'space-between',
            gap: 12,
          }}
        >
          <View>
            <strong>{summary.goal.name}</strong>
            <span style={helpStyle}>{formatGoalKind(summary.goal)}</span>
          </View>
          <FinancialText as="strong">
            {formatProductMoney(summary.targetAmount)}
          </FinancialText>
        </View>
        {error !== null && (
          <p role="alert" style={errorStyle}>
            {error}
          </p>
        )}
        <View style={buttonRowStyle}>
          <Button onPress={onEdit} style={{ minHeight: 42 }}>
            <Trans>Edit</Trans>
          </Button>
          <ButtonWithLoading
            isLoading={isDeleting}
            onPress={() => void remove()}
            style={{ minHeight: 42 }}
          >
            <Trans>Delete</Trans>
          </ButtonWithLoading>
        </View>
      </View>
    </Card>
  );
}

export function GoalForm({
  state,
  editing,
  client,
  onCancel,
  onSaved,
}: Readonly<{
  state: FinancialManagementState;
  editing: ManagedGoal | null;
  client: ProductFinanceClient;
  onCancel: () => void;
  onSaved: () => Promise<void>;
}>) {
  const { t } = useTranslation();
  const current = editing?.goal;
  const initial = getGoalFormValues(current);
  const [name, setName] = useState(current?.name ?? '');
  const [kind, setKind] = useState<GoalKind>(current?.kind ?? 'emergency-fund');
  const [strategy, setStrategy] = useState<EmergencyStrategy>(initial.strategy);
  const [baseAmount, setBaseAmount] = useState(initial.baseAmount);
  const [multiplier, setMultiplier] = useState(initial.multiplier);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const amountMinorUnits = parseMoneyInput(baseAmount);
    const count = Number(multiplier);
    if (
      name.trim().length === 0 ||
      amountMinorUnits === null ||
      !Number.isSafeInteger(count) ||
      count <= 0
    ) {
      setError(t('Enter a name, positive amount, and positive whole number.'));
      return;
    }
    const input: FinancialGoalRequest = {
      ...(current === undefined ? {} : { id: current.id }),
      name: name.trim(),
      definition: createGoalDefinition(
        kind,
        strategy,
        amountMinorUnits,
        count,
        state.settings.currency,
      ),
    };
    setIsSubmitting(true);
    setError(null);
    try {
      if (current === undefined) await client.createFinancialGoal(input);
      else await client.updateFinancialGoal(input);
      await onSaved();
    } catch (submitError) {
      setError(getManagementErrorMessage(submitError, t));
    } finally {
      setIsSubmitting(false);
    }
  }

  return (
    <ManagementCard
      title={
        current === undefined ? (
          <Trans>Add financial goal</Trans>
        ) : (
          <Trans>Edit financial goal</Trans>
        )
      }
    >
      <form onSubmit={submit} style={formStyle}>
        <Field label={<Trans>Name</Trans>} htmlFor="goal-name">
          <input
            id="goal-name"
            value={name}
            onChange={event => setName(event.target.value)}
            style={inputStyle}
          />
        </Field>
        <Field label={<Trans>Goal type</Trans>} htmlFor="goal-kind">
          <select
            id="goal-kind"
            aria-label={t('Goal type')}
            value={kind}
            onChange={event => setKind(readGoalKind(event.target.value))}
            style={inputStyle}
          >
            <option value="emergency-fund">
              <Trans>Emergency fund</Trans>
            </option>
            <option value="custom-target">
              <Trans>Custom target</Trans>
            </option>
          </select>
        </Field>
        {kind === 'emergency-fund' && (
          <Field label={<Trans>Calculation</Trans>} htmlFor="goal-strategy">
            <select
              id="goal-strategy"
              aria-label={t('Calculation')}
              value={strategy}
              onChange={event =>
                setStrategy(readEmergencyStrategy(event.target.value))
              }
              style={inputStyle}
            >
              <option value="salary-multiple">
                <Trans>Monthly salary multiple</Trans>
              </option>
              <option value="essential-expense-months">
                <Trans>Months of essential expenses</Trans>
              </option>
              <option value="fixed-amount">
                <Trans>Fixed amount</Trans>
              </option>
            </select>
          </Field>
        )}
        <Field label={getGoalAmountLabel(kind, strategy)} htmlFor="goal-amount">
          <input
            id="goal-amount"
            inputMode="decimal"
            value={baseAmount}
            onChange={event => setBaseAmount(event.target.value)}
            style={inputStyle}
          />
        </Field>
        {kind === 'emergency-fund' && strategy !== 'fixed-amount' && (
          <Field
            label={
              strategy === 'salary-multiple' ? (
                <Trans>Salary multiple</Trans>
              ) : (
                <Trans>Number of months</Trans>
              )
            }
            htmlFor="goal-multiplier"
          >
            <input
              id="goal-multiplier"
              type="number"
              min="1"
              step="1"
              value={multiplier}
              onChange={event => setMultiplier(event.target.value)}
              style={inputStyle}
            />
          </Field>
        )}
        {error !== null && (
          <p role="alert" style={errorStyle}>
            {error}
          </p>
        )}
        <View style={buttonRowStyle}>
          <ButtonWithLoading
            variant="primary"
            type="submit"
            isLoading={isSubmitting}
            style={{ minHeight: 42 }}
          >
            <Trans>Save goal</Trans>
          </ButtonWithLoading>
          <Button onPress={onCancel} style={{ minHeight: 42 }}>
            <Trans>Cancel</Trans>
          </Button>
        </View>
      </form>
    </ManagementCard>
  );
}

export function createGoalDefinition(
  kind: GoalKind,
  strategy: EmergencyStrategy,
  amountMinorUnits: number,
  count: number,
  currency: string,
): FinancialGoalRequest['definition'] {
  if (kind === 'custom-target') {
    return { kind, targetAmountMinorUnits: amountMinorUnits, currency };
  }
  switch (strategy) {
    case 'salary-multiple':
      return {
        kind,
        strategy: {
          kind: strategy,
          monthlySalaryMinorUnits: amountMinorUnits,
          multiple: count,
          currency,
        },
      };
    case 'essential-expense-months':
      return {
        kind,
        strategy: {
          kind: strategy,
          monthlyEssentialExpensesMinorUnits: amountMinorUnits,
          months: count,
          currency,
        },
      };
    case 'fixed-amount':
      return {
        kind,
        strategy: {
          kind: strategy,
          targetAmountMinorUnits: amountMinorUnits,
          currency,
        },
      };
    default:
      return assertNever(strategy);
  }
}

export function getGoalFormValues(goal: ManagedGoal['goal'] | undefined): {
  strategy: EmergencyStrategy;
  baseAmount: string;
  multiplier: string;
} {
  if (goal === undefined) {
    return { strategy: 'salary-multiple', baseAmount: '', multiplier: '6' };
  }
  if (goal.kind === 'custom-target') {
    return {
      strategy: 'salary-multiple',
      baseAmount: formatMoneyInput(goal.targetAmount),
      multiplier: '1',
    };
  }
  switch (goal.strategy.kind) {
    case 'salary-multiple':
      return {
        strategy: goal.strategy.kind,
        baseAmount: formatMoneyInput(goal.strategy.monthlySalary),
        multiplier: String(goal.strategy.multiple),
      };
    case 'essential-expense-months':
      return {
        strategy: goal.strategy.kind,
        baseAmount: formatMoneyInput(goal.strategy.monthlyEssentialExpenses),
        multiplier: String(goal.strategy.months),
      };
    case 'fixed-amount':
      return {
        strategy: goal.strategy.kind,
        baseAmount: formatMoneyInput(goal.strategy.targetAmount),
        multiplier: '1',
      };
    default:
      return assertNever(goal.strategy);
  }
}

export function getGoalAmountLabel(
  kind: GoalKind,
  strategy: EmergencyStrategy,
): ReactNode {
  if (kind === 'custom-target' || strategy === 'fixed-amount') {
    return <Trans>Target amount</Trans>;
  }
  return strategy === 'salary-multiple' ? (
    <Trans>Monthly salary</Trans>
  ) : (
    <Trans>Monthly essential expenses</Trans>
  );
}

export function formatGoalKind(goal: ManagedGoal['goal']): ReactNode {
  if (goal.kind === 'custom-target') {
    return <Trans>Custom target</Trans>;
  }
  switch (goal.strategy.kind) {
    case 'salary-multiple':
      return <Trans>Emergency fund · salary multiple</Trans>;
    case 'essential-expense-months':
      return <Trans>Emergency fund · essential expense months</Trans>;
    case 'fixed-amount':
      return <Trans>Emergency fund · fixed amount</Trans>;
    default:
      return assertNever(goal.strategy);
  }
}

export function readGoalKind(value: string): GoalKind {
  if (value === 'emergency-fund' || value === 'custom-target') {
    return value;
  }
  throw new Error('Unsupported financial goal kind.');
}

export function readEmergencyStrategy(value: string): EmergencyStrategy {
  if (
    value === 'salary-multiple' ||
    value === 'essential-expense-months' ||
    value === 'fixed-amount'
  ) {
    return value;
  }
  throw new Error('Unsupported emergency fund strategy.');
}
